import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { delay, filter, map, tap } from 'rxjs/operators';
import { KzTutorialDirective } from './kz-tutorial.directive';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { Subscription } from 'rxjs/internal/Subscription';
import { CookieService } from 'ngx-cookie';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable()
export class KzTutorialService {

  private _tutorialItems: KzTutorials = {};
  private _currentItem: BehaviorSubject<number> = new BehaviorSubject(0);
  private _initService: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private subscription: Subscription;

  constructor(
      private readonly cookieService: CookieService,
      @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  get tutorialItems(): KzTutorials {
    return this._tutorialItems;
  }

  set tutorialItems(kzTutorials: KzTutorials) {
    this._tutorialItems = kzTutorials;
  }

  get currentItem(): number {
    return this._currentItem.getValue();
  }

  set currentItem(index: number) {
    this._currentItem.next(index);
  }

  initTutorialService(tutorial = 'default') {
    this.subscription = of(true).pipe(
        switchMap(() => this._currentItem.asObservable().pipe(
            switchMap((currentItem) => {
              if (this._initService.getValue() === true) {
                this._initService.next(false);
                return of(true).pipe(delay(1500), tap(() => this.orderItems(tutorial)), map(() => currentItem));
              } else {
                return of(currentItem);
              }
            })
        )),
        filter((currentItem) => currentItem > 0),
        tap((currentItem) => this.tutorialItems[tutorial][currentItem - 1].open(currentItem, this.tutorialItems[tutorial].length))
    ).subscribe();
  }

  registerItem(tutorial: string, item: KzTutorialDirective) {
    if (this.tutorialItems.hasOwnProperty(tutorial)) {
      this.tutorialItems[tutorial].push(item);
    } else {
      this.tutorialItems[tutorial] = [item];
    }
  }

  orderItems(tutorial = 'default') {
    this.tutorialItems[tutorial].sort((a, b) => a.tutorialStep > b.tutorialStep && 1 || -1);
  }

  unregisterItem(tutorial: string, item: KzTutorialDirective) {
    if(this.tutorialItems && this.tutorialItems[tutorial]) {
      this.tutorialItems[tutorial] = this.tutorialItems[tutorial].filter((tutorialDirective) => tutorialDirective !== item);
    }
  }

  hideTutorial(tutorial = 'default') {
    this.cookieService.put(`kz_tutorial_${tutorial}_hide`, 'true');
  }

  checkRules(ruleMaxTimes: number, tutorial = 'default'): boolean {
    if (this.cookieService.hasKey(`kz_tutorial_${tutorial}_hide`)
        || (isPlatformServer(this.platformId))
        || (isPlatformBrowser(this.platformId) && window.innerWidth <= 799)) {
      return false;
    }
    const times = (this.cookieService.hasKey(`kz_tutorial_${tutorial}_time_displayed`))
        ? parseInt(this.cookieService.get(`kz_tutorial_${tutorial}_time_displayed`))
        : 0;
    if (times >= ruleMaxTimes) {
      this.hideTutorial(tutorial);
      this.cookieService.remove(`kz_tutorial_${tutorial}_time_displayed`);
      return false;
    }
    this.cookieService.put(`kz_tutorial_${tutorial}_time_displayed`, `${times + 1}`);
    return true;
  }

  showItems(ruleMaxTimes = 5, tutorial = 'default') {
    if (!this.checkRules(ruleMaxTimes, tutorial)) {
      return;
    }
    this._initService.next(true);
    this.currentItem = 1;
  }

  destroyItems(tutorial = 'default') {
    this.tutorialItems[tutorial] = [];
    this.currentItem = 0;
    this.unsub();
  }

  nextItem() {
    this.currentItem = this.currentItem + 1;
  }
  previousItem() {
    this.currentItem = this.currentItem - 1;
  }

  private unsub() {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
  }
  ngOnDestroy() {
    this.unsub();
  }
}

export type KzTutorials = {
  [key:string]: Array<KzTutorialDirective>;
}
